import { Fragment, ReactNode } from "react";
import { Box, Paper, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import { Link } from "react-router-dom";

export type InfoListColDef = {
  label: string;
  context?: ReactNode;
  targetUrl?: URL | null;
};

export function InfoList({
  cols,
  labelWidth = 120,
  columns = 3,
}: {
  cols: InfoListColDef[];
  labelWidth?: number;
  columns?: 1 | 2 | 3;
}) {
  const rows: (InfoListColDef | null)[][] = [];
  for (let i = 0; i < cols.length; i += columns) {
    const row: (InfoListColDef | null)[] = [];
    for (let j = 0; j < columns; j++) {
      row.push(cols[i + j] ?? null);
    }
    rows.push(row);
  }

  return (
    <Paper variant="outlined" sx={{ overflow: "hidden" }}>
      <Table size="small">
        <TableBody>
          {rows.map((row, rowIdx) => (
            <TableRow key={rowIdx} sx={{ "&:last-of-type td": { borderBottom: 0 } }}>
              {row.map((col, colIdx) => {
                const isLast = colIdx === columns - 1;
                if (!col) {
                  return (
                    <Fragment key={colIdx}>
                      <TableCell
                        sx={{
                          width: labelWidth,
                          bgcolor: "grey.50",
                          borderRight: isLast ? 0 : 1,
                          borderColor: "divider",
                          py: 1,
                        }}
                      />
                      <TableCell sx={{ py: 1, borderRight: isLast ? 0 : 1, borderColor: "divider" }} />
                    </Fragment>
                  );
                }
                return (
                  <Fragment key={colIdx}>
                    <LabelCell label={col.label} width={labelWidth} />
                    <TableCell sx={{ py: 1, borderRight: isLast ? 0 : 1, borderColor: "divider" }}>
                      <ValueCell col={col} />
                    </TableCell>
                  </Fragment>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function LabelCell({ label, width }: { label: string; width: number }) {
  return (
    <TableCell
      sx={{
        width,
        bgcolor: "grey.100",
        borderRight: 1,
        borderColor: "divider",
        fontWeight: 600,
        color: "text.secondary",
        whiteSpace: "nowrap",
        py: 1,
      }}
    >
      {label}
    </TableCell>
  );
}

function ValueCell({ col }: { col: InfoListColDef }) {
  const content = typeof col.context === "string" || typeof col.context === "number"
    ? <Typography variant="body2">{col.context}</Typography>
    : <Box component="span">{col.context}</Box>;

  if (!col.targetUrl) {
    return content;
  }

  return (
    <Link
      to={{ pathname: col.targetUrl.pathname, search: col.targetUrl.search }}
      style={{ textDecoration: "underline" }}
    >
      {typeof col.context === "string" || typeof col.context === "number" ? (
        <Typography variant="body2" component="span" color="primary.main">
          {col.context}
        </Typography>
      ) : (
        <Box component="span">{col.context}</Box>
      )}
    </Link>
  );
}
